import { type CSSObject } from '@emotion/react';

const AutofillStates = ['hover', 'focus', 'active'] as const;
type AutofillState = (typeof AutofillStates)[number];

/**
 * Overrides the text color that is applied by the browser for an autofilled input element. This
 * maintains the text color of the input.
 */
const AutofillInputFill = 'inherit !important';

/**
 * Applies a long transition to hide the background change/effects that the browser applies to the
 * input element when it is autofilled.
 */
const AutofillTransition = 'background-color 9999s ease-out !important';

const AutofillStyleOverride = {
  transition: AutofillTransition,
  WebkitTextFillColor: AutofillInputFill,
};

const inputSelector = (state?: AutofillState): string =>
  state === undefined ? 'input:-webkit-autofill' : `${inputSelector()}:${state}`;

/**
 * Defines CSS overrides that are applied to the {@link MuiCssBaseline} component via the theme,
 * {@link Theme}.
 *
 * The style defined in this overrides is used to prevent browser-specific styling that is applied
 * during auto-complete from affecting the appearance of form elements in the application.
 *
 * @see {@link https://mui.com/material-ui/api/css-baseline/}
 * @see {@link https://mui.com/material-ui/customization/theming/#css-baseline}
 */
export const AutofillCssOverrides: CSSObject = AutofillStates.reduce(
  (acc: CSSObject, selector) => ({ ...acc, [inputSelector(selector)]: AutofillStyleOverride }),
  { [inputSelector()]: AutofillStyleOverride },
);
